'use client';

import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useUserDataContext } from '@/components/navigation/contexts/user-data-context';
import type { Team } from '@/services/teams.service';
import { setActiveTeam } from '@/services/teams.service';

export function TeamSwitcher() {
  const { team, userTeams, userDetails } = useUserDataContext();
  const queryClient = useQueryClient();
  const [isSwitching, setIsSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const switchTeam = async (teamId: string) => {
    if (
      isSwitching ||
      teamId === team?.id ||
      !userTeams.some(
        (option: Team) => option.id === teamId && option.status === 'active',
      )
    )
      return;
    setIsSwitching(true);
    setError(null);
    try {
      await setActiveTeam(teamId);
      await queryClient.cancelQueries();
      queryClient.clear();
      try {
        window.localStorage.setItem(
          'clearitty:team-context-changed',
          JSON.stringify({
            userId: String(userDetails?.id),
            teamId,
            timestamp: Date.now(),
          }),
        );
      } catch {
        window.location.reload();
        return;
      }
      window.location.reload();
    } catch {
      setError(
        'Unable to switch teams. Make sure your invitation is accepted and try again.',
      );
      setIsSwitching(false);
    }
  };

  const availableTeams = (userTeams as Team[]).filter(
    (option) => option.status === 'active',
  );
  if (!availableTeams.length) return null;

  return (
    <div className="min-w-0 space-y-1">
      <label className="flex min-w-0 items-center gap-2 text-sm text-[#7776b5]">
        <span className="shrink-0">Account</span>
        <select
          className="h-9 w-full min-w-0 max-w-56 rounded-md border border-[#e3e7f2] bg-white px-3 text-[#0b0d3b] disabled:opacity-60"
          disabled={isSwitching || availableTeams.length < 2}
          value={team?.id ?? ''}
          onChange={(event) => void switchTeam(event.target.value)}
        >
          {!team && (
            <option value="" disabled>
              Select a team
            </option>
          )}
          {availableTeams.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </select>
      </label>
      {isSwitching && (
        <p role="status" className="text-xs">
          Switching teams…
        </p>
      )}
      {error && (
        <p role="alert" className="max-w-sm text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
